import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout, { usePageTitle } from '../components/site/Layout';

const PARTNERS_EMAIL = 'partners@comparevin.com';
// Optional: create a free form at formspree.io and set REACT_APP_FORMSPREE_ID on Vercel to receive
// submissions as real form posts. Without it the form falls back to a pre-filled email.
const FORMSPREE_ID = process.env.REACT_APP_FORMSPREE_ID;

const SERVICE_TYPES = [
  'Full vehicle history report',
  'Title / NMVTIS data only',
  'Auction & salvage photos',
  'Imports / international records',
  'Subscription or unlimited plan',
  'Free tool',
  'Other',
];

const REQUIREMENTS = [
  ['US market', 'You serve buyers of vehicles in the United States.'],
  ['Real VIN-based data', 'Your product returns data tied to a VIN, and you can tell us where it comes from.'],
  ['Public pricing', 'Prices and refund or renewal terms are published on your site.'],
  ['A working product', 'People can buy or use it today — no waitlists.'],
];

const EMPTY = { company: '', website: '', name: '', email: '', type: SERVICE_TYPES[0], price: '', affiliate: 'Not sure', affiliateUrl: '', subject: '', message: '', trap: '', ack: false };

function mailtoFor(f) {
  const body = [
    `Company: ${f.company}`,
    `Website: ${f.website}`,
    `Contact: ${f.name} <${f.email}>`,
    `Type of service: ${f.type}`,
    `Starting price: ${f.price || '—'}`,
    `Affiliate program: ${f.affiliate}${f.affiliateUrl ? ` (${f.affiliateUrl})` : ''}`,
    '',
    f.message,
  ].join('\n');
  return `mailto:${PARTNERS_EMAIL}?subject=${encodeURIComponent(`[Get listed] ${f.subject || f.company}`)}&body=${encodeURIComponent(body)}`;
}

export default function GetListed() {
  usePageTitle('Get Listed — Submit Your Vehicle History Service | CompareVIN');
  const [f, setF] = useState(EMPTY);
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [error, setError] = useState('');
  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.trap) return; // honeypot: bots fill the hidden field
    if (!f.company.trim() || !f.website.trim() || !f.name.trim() || !/^\S+@\S+\.\S+$/.test(f.email) || !f.subject.trim() || !f.message.trim()) {
      setError('Please fill in the company, website, your name, a valid email, a subject and a message.');
      return;
    }
    if (!f.ack) {
      setError('Please confirm you have read how listing works.');
      return;
    }
    setError('');
    if (!FORMSPREE_ID) {
      window.location.href = mailtoFor(f);
      setStatus('sent');
      return;
    }
    setStatus('sending');
    try {
      const res = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...f, trap: undefined, ack: undefined, _subject: `[Get listed] ${f.subject}`, _replyto: f.email }),
      });
      if (!res.ok) throw new Error('bad status');
      setStatus('sent');
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <Layout>
      <section className="cv-hero">
        <div className="cv-wrap">
          <div className="cv-eyebrow">For providers</div>
          <h1>Get your service listed.</h1>
          <p className="cv-lead">Run a vehicle history, title or VIN data service for the US market? Tell us about it. We review every submission by hand, and add the ones that would genuinely help car buyers.</p>
        </div>
      </section>

      <section className="cv-section" style={{ paddingTop: 48 }}>
        <div className="cv-wrap cv-listed">
          <div>
            {status === 'sent' ? (
              <div className="cv-panel" role="status">
                <h3>{FORMSPREE_ID ? 'Thanks — we have your submission.' : 'Your email app should have opened.'}</h3>
                <p style={{ marginTop: 10, color: 'var(--ink-2)' }}>
                  {FORMSPREE_ID
                    ? 'We read every one. If it looks like a fit we will write to the address you gave us.'
                    : <>Send the pre-filled message to complete your submission. Nothing opened? Email us directly at <a href={`mailto:${PARTNERS_EMAIL}`}>{PARTNERS_EMAIL}</a>.</>}
                </p>
                <p style={{ marginTop: 16 }}><button className="cv-link-btn" onClick={() => { setF(EMPTY); setStatus('idle'); }}>Submit another</button></p>
              </div>
            ) : (
              <form className="cv-panel cv-form" onSubmit={submit} noValidate>
                <h3>Submit your service</h3>
                <p className="cv-panel-sub">All fields are required unless marked optional.</p>

                <div className="cv-form-grid">
                  <label><span>Company name</span><input className="cv-input" value={f.company} onChange={set('company')} autoComplete="organization" /></label>
                  <label><span>Website</span><input className="cv-input" value={f.website} onChange={set('website')} placeholder="https://" inputMode="url" autoComplete="url" /></label>
                  <label><span>Your name</span><input className="cv-input" value={f.name} onChange={set('name')} autoComplete="name" /></label>
                  <label><span>Work email</span><input className="cv-input" type="email" value={f.email} onChange={set('email')} autoComplete="email" /></label>
                  <label><span>Type of service</span>
                    <select className="cv-input" value={f.type} onChange={set('type')}>
                      {SERVICE_TYPES.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </label>
                  <label><span>Starting price <span className="cv-opt">(optional)</span></span><input className="cv-input" value={f.price} onChange={set('price')} placeholder="e.g. $9.99 per report" /></label>
                  <label><span>Affiliate program?</span>
                    <select className="cv-input" value={f.affiliate} onChange={set('affiliate')}>
                      <option>Not sure</option><option>Yes</option><option>No</option>
                    </select>
                  </label>
                  <label><span>Affiliate sign-up link <span className="cv-opt">(optional)</span></span><input className="cv-input" value={f.affiliateUrl} onChange={set('affiliateUrl')} placeholder="https://" inputMode="url" /></label>
                </div>

                <label className="cv-form-full"><span>Subject</span><input className="cv-input" value={f.subject} onChange={set('subject')} placeholder="e.g. Add ExampleVIN to your report guide" /></label>
                <label className="cv-form-full"><span>Tell us about your data</span>
                  <textarea className="cv-input cv-textarea" value={f.message} onChange={set('message')} rows={6} placeholder="Where does your data come from? What can it show that others can't? Any limits we should be upfront about?" />
                </label>

                <div className="cv-hp" aria-hidden="true"><label>Leave this empty<input tabIndex={-1} autoComplete="off" value={f.trap} onChange={set('trap')} /></label></div>

                <label className="cv-check">
                  <input type="checkbox" checked={f.ack} onChange={set('ack')} />
                  <span>I understand that submitting is free, that a listing can't be bought, and that CompareVIN may decline any submission.</span>
                </label>

                {error && <div className="cv-error" role="alert">{error}</div>}
                {status === 'error' && <div className="cv-error" role="alert">Something went wrong sending that. Please email <a href={mailtoFor(f)}>{PARTNERS_EMAIL}</a> instead.</div>}

                <div style={{ marginTop: 18 }}>
                  <button className="cv-btn" type="submit" disabled={status === 'sending'}>{status === 'sending' ? 'Sending…' : 'Submit for review'}</button>
                </div>
              </form>
            )}
          </div>

          <aside>
            <div className="cv-panel">
              <h3>How listing works</h3>
              <ul className="cv-checklist cv-checklist--ticks" style={{ marginTop: 14 }}>
                <li>Submitting is free.</li>
                <li>Listing can't be bought. Payment never changes where a service appears.</li>
                <li>We review by hand, and may ask you for sample reports.</li>
                <li>We say plainly what a service does not cover.</li>
              </ul>
              <p className="cv-muted" style={{ marginTop: 14, fontSize: 14 }}>Read the <Link to="/how-we-work">full policy</Link>.</p>
            </div>
            <div className="cv-panel">
              <h3>What we look for</h3>
              <div style={{ display: 'grid', gap: 14, marginTop: 14 }}>
                {REQUIREMENTS.map(([t, d]) => (
                  <div key={t}><b>{t}</b><p style={{ fontSize: 14.5, color: 'var(--ink-2)' }}>{d}</p></div>
                ))}
              </div>
            </div>
            <p className="cv-muted" style={{ fontSize: 14 }}>Prefer email? <a href={`mailto:${PARTNERS_EMAIL}`}>{PARTNERS_EMAIL}</a></p>
          </aside>
        </div>
      </section>
    </Layout>
  );
}
